/**
 * Infrastructure Module Types (Frontend)
 *
 * Feature: 017-backup-infrastructure-setup
 * Mirrors backend types for type-safe API communication
 */

// =============================================================================
// Enums and Constants
// =============================================================================

export type Environment = 'DEV' | 'UAT' | 'PROD';
export type CapacityType = 'Reference' | 'ClientDedicated' | 'MultiTenant' | 'Platform';
export type JobPriority = 'Normal' | 'High' | 'Critical';
export type RestartPolicy = 'Never' | 'OnFailure' | 'Always';
export type HealthStatus = 'healthy' | 'degraded' | 'unavailable' | 'unknown';
export type HealthComponent = 'acr' | 'aci' | 'keyvault' | 'storage';
export type ValidationStatus = 'valid' | 'invalid' | 'unknown';

export const CAPACITY_TYPES: CapacityType[] = [
  'Reference',
  'ClientDedicated',
  'MultiTenant',
  'Platform',
];
export const JOB_PRIORITIES: JobPriority[] = ['Normal', 'High', 'Critical'];
export const RESTART_POLICIES: RestartPolicy[] = ['Never', 'OnFailure', 'Always'];
export const HEALTH_STATUSES: HealthStatus[] = ['healthy', 'degraded', 'unavailable', 'unknown'];
export const HEALTH_COMPONENTS: HealthComponent[] = ['acr', 'aci', 'keyvault', 'storage'];

// =============================================================================
// Core Infrastructure Types
// =============================================================================

export interface InfrastructureItem {
  id: string;
  name: string;
  type: string;
  status: HealthStatus;
  capacity_id: string;
  workspace_id?: string;
  created_at: Date;
  updated_at: Date;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// API DTOs
// =============================================================================

export interface AcrConfigDto {
  registryName: string | null;
  resourceGroup: string | null;
  subscriptionId: string | null;
  loginServer?: string | null;
}

export interface AciDefaultsDto {
  cpuCores: number;
  memoryGb: number;
  timeoutMinutes: number;
  restartPolicy: RestartPolicy;
  location: string;
}

export interface AciConfigDto {
  resourceGroup: string;
  subscriptionId: string;
  defaults: AciDefaultsDto;
}

export interface KeyVaultConfigDto {
  url: string;
  useManagedIdentity: boolean;
}

export interface HealthConfigDto {
  status: HealthStatus;
  lastCheck: string | null;
}

export interface AciCapacityOverrideDto {
  id?: string;
  capacityType: CapacityType;
  cpuCores: number | null;
  memoryGb: number | null;
  timeoutMinutes: number | null;
  restartPolicy: RestartPolicy | null;
  priority: JobPriority;
  maxConcurrentJobs: number;
  isActive: boolean;
}

export interface KeyVaultReferenceDto {
  id?: string;
  purpose: string;
  secretName: string;
  description: string | null;
  isRequired: boolean;
  validationStatus: ValidationStatus;
  lastValidated?: string | null;
}

export interface InfrastructureConfigDto {
  id: string;
  environment: Environment;
  acr: AcrConfigDto;
  aci: AciConfigDto;
  keyVault: KeyVaultConfigDto;
  health: HealthConfigDto;
  capacityOverrides: AciCapacityOverrideDto[];
  keyVaultReferences: KeyVaultReferenceDto[];
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
}

export interface InfrastructureConfigUpdateDto {
  acr?: AcrConfigDto;
  aci: {
    resourceGroup: string;
    subscriptionId: string;
    defaults: AciDefaultsDto;
  };
  keyVault: KeyVaultConfigDto;
}

// =============================================================================
// Health Check Types
// =============================================================================

export interface ComponentHealthStatus {
  status: HealthStatus;
  message: string | null;
  responseTimeMs?: number;
}

export interface HealthCheckResult {
  overall: HealthStatus;
  lastCheck?: string;
  checkedAt?: string;
  duration?: number;
  /** Runtime environment (DEV/UAT/PROD) reported by the backend */
  environment?: Environment;
  /** The actual Azure resources being probed (from infrastructure_config) */
  resources?: {
    keyVaultUrl?: string | null;
    resourceGroup?: string | null;
    subscriptionId?: string | null;
    registry?: string | null;
    region?: string | null;
  };
  components: {
    acr: ComponentHealthStatus;
    aci: ComponentHealthStatus;
    keyVault: ComponentHealthStatus;
    storage: ComponentHealthStatus;
  };
}

// =============================================================================
// Spec 216 T024/T025 — collected (DB-only) health read model. Replaces the
// live-probe HealthCheckResult on GET /health; every component carries how it
// is judged and when its data was collected (or an explicit not-collected).
// =============================================================================

export type CollectedSignal = 'ok' | 'attention' | 'stale' | 'not-collected';

export interface CollectedHealthComponent {
  component: string;
  signal: CollectedSignal;
  criterion: string;
  lastCollectedAt: string | null;
  message: string | null;
  latencyMs: number | null;
}

export interface CollectedHealth {
  environment: Environment;
  mission: string;
  overall: CollectedSignal;
  components: CollectedHealthComponent[];
  resources: {
    keyVaultUrl?: string | null;
    resourceGroup?: string | null;
    subscriptionId?: string | null;
    registry?: string | null;
    region?: string | null;
  } | null;
}

export interface HealthHistoryItem {
  id: string;
  component: HealthComponent;
  status: HealthStatus;
  message: string | null;
  responseTimeMs: number | null;
  checkedAt: string;
}

export interface HealthHistoryResponse {
  items: HealthHistoryItem[];
  total: number;
}

// =============================================================================
// Validation Types
// =============================================================================

export interface ValidationCheckResult {
  status: HealthStatus;
  message: string;
  responseTimeMs?: number;
}

export interface ConfigValidationResult {
  valid: boolean;
  checks: {
    acr?: ValidationCheckResult;
    aci?: ValidationCheckResult;
    keyVault?: ValidationCheckResult;
  };
}

export interface KeyVaultValidationResult {
  valid: boolean;
  references: {
    purpose: string;
    status: ValidationStatus;
    message: string | null;
  }[];
}

// =============================================================================
// Form State Types
// =============================================================================

export interface AcrFormData {
  registryName: string;
  resourceGroup: string;
  subscriptionId: string;
}

export interface AciDefaultsFormData {
  resourceGroup: string;
  subscriptionId: string;
  cpuCores: number;
  memoryGb: number;
  timeoutMinutes: number;
  restartPolicy: RestartPolicy;
  location: string;
}

export interface KeyVaultFormData {
  url: string;
  useManagedIdentity: boolean;
}

export interface OverrideFormData {
  capacityType: CapacityType;
  cpuCores: number | null;
  memoryGb: number | null;
  timeoutMinutes: number | null;
  restartPolicy: RestartPolicy | null;
  priority: JobPriority;
  maxConcurrentJobs: number;
  isActive: boolean;
}

export interface KeyVaultReferenceFormData {
  purpose: string;
  secretName: string;
  description: string;
  isRequired: boolean;
}

// =============================================================================
// Azure Discovery Types
// =============================================================================

export interface DiscoveredSubscription {
  subscriptionId: string;
  displayName: string;
  state: string;
}

export interface DiscoveredResourceGroup {
  name: string;
  location: string;
}

export interface DiscoveredRegistry {
  name: string;
  resourceGroup: string;
  loginServer: string;
  location: string;
  sku: string | undefined;
}

export interface DiscoveredKeyVault {
  name: string;
  resourceGroup: string;
  vaultUri: string;
  location: string;
}

export interface DiscoveryResult {
  subscriptionId: string;
  registries: DiscoveredRegistry[];
  keyVaults: DiscoveredKeyVault[];
  errors: { source: string; message: string }[];
}

export interface DiscoverySelection {
  registry: DiscoveredRegistry | null;
  keyVault: DiscoveredKeyVault | null;
  aciResourceGroup: string;
  aciSubscriptionId: string;
}
